import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import useFeedbackMonitor from "../custom-hooks/useFeedbackMonitor";

const ITEMS_PER_PAGE = 10;
const DONATION_URL =
  "https://www.givecampus.com/campaigns/47387/donations/new?a=8830926&designation=089252";
const BROWSE_DONATION_CLICK_COUNT_KEY = "browseDonationClickCount";
const BROWSE_DONATION_PROMPT_SHOWN_KEY = "browseDonationPromptShown";
const DONATION_PROMPT_CLICK_COUNT = 5;

const useBrowseResults = ({ countDonationClicks = false } = {}) => {
  const location = useLocation();

  const [browseResults, setBrowseResults] = useState(
    location.state?.browseResults || []
  );
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);
  const [donationPrompt, setDonationPrompt] = useState({
    show: false,
    pendingRow: null,
  });

  const totalPages = Math.ceil(browseResults.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const displayedRows = browseResults.slice(startIndex, endIndex);
  const emptyRowsCount = ITEMS_PER_PAGE - displayedRows.length;

  const {
    showFeedbackPopup,
    handleCloseFeedbackPopup,
    fingerprint,
    isNewUser,
  } = useFeedbackMonitor();

  useEffect(() => {
    setCurrentPage(1);
  }, [location.state?.browseResults]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const shouldShowDonationPrompt = () => {
    if (!countDonationClicks) {
      return false;
    }

    const hasShownDonationPrompt =
      localStorage.getItem(BROWSE_DONATION_PROMPT_SHOWN_KEY) === "true";

    if (hasShownDonationPrompt) {
      return false;
    }

    const currentCount =
      Number(localStorage.getItem(BROWSE_DONATION_CLICK_COUNT_KEY)) || 0;
    const nextCount = currentCount + 1;

    localStorage.setItem(
      BROWSE_DONATION_CLICK_COUNT_KEY,
      JSON.stringify(nextCount)
    );

    if (nextCount === DONATION_PROMPT_CLICK_COUNT) {
      localStorage.setItem(BROWSE_DONATION_PROMPT_SHOWN_KEY, "true");
      return true;
    }

    return false;
  };

  const openRow = (row) => {
    setSelectedRow(row);
    setModalOpen(true);
  };

  const handleRowClick = (row) => {
    if (shouldShowDonationPrompt()) {
      setDonationPrompt({
        show: true,
        pendingRow: row,
      });
      return;
    }

    openRow(row);
  };

  const handleDonationPromptClose = () => {
    setDonationPrompt({
      show: false,
      pendingRow: null,
    });
  };

  const handleDonationPromptContinue = () => {
    const { pendingRow } = donationPrompt;
    handleDonationPromptClose();

    if (pendingRow) {
      openRow(pendingRow);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleCloseAnalyticsModal = () => {
    setAnalyticsModalOpen(false);
  };

  const countAttributes = (attribute) => {
    const attributeCounts = [];
    browseResults.forEach((result) => {
      const value = result[attribute];
      const index = attributeCounts.findIndex(
        (obj) => obj.attribute === attribute && obj.value === value
      );
      if (index === -1) {
        attributeCounts.push({ attribute, value, count: 1 });
      } else {
        attributeCounts[index].count += 1;
      }
    });
    return attributeCounts;
  };

  const collTypeCounts = countAttributes("coll_type");
  const collSourceCounts = countAttributes("coll_source");
  const collLocationCounts = countAttributes("coll_location");

  const collNameIdentifier = browseResults ? countAttributes("coll_name") : [];

  useEffect(() => {
    const handlePageHide = () => {
      localStorage.setItem("browseItemsSet", false);
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);

  return {
    browseResults,
    setBrowseResults,
    loading,
    setLoading,
    currentPage,
    setCurrentPage,
    totalPages,
    displayedRows,
    emptyRowsCount,
    handlePageChange,
    modalOpen,
    setModalOpen,
    selectedRow,
    setSelectedRow,
    handleRowClick,
    donationPrompt,
    donationUrl: DONATION_URL,
    handleDonationPromptClose,
    handleDonationPromptContinue,
    handleCloseModal,
    analyticsModalOpen,
    setAnalyticsModalOpen,
    handleCloseAnalyticsModal,
    showFeedbackPopup,
    handleCloseFeedbackPopup,
    fingerprint,
    isNewUser,
    collTypeCounts,
    collSourceCounts,
    collLocationCounts,
    collNameIdentifier,
  };
};

export default useBrowseResults;
